import { signInWithRedirect, signUp, resendSignUpCode, confirmSignUp, updateUserAttributes, updatePassword, signOut } from 'aws-amplify/auth';
import { nanoid } from 'nanoid';
import namedBaselines from '../../misc/namedBaselines';
import { getAuthSession, getAuthenticatedUser, getUserAttributes } from '../../amplify/authUtilities';

const defaultBaselineDate = {
  name: 'sameTimeLastYear',
  date: namedBaselines.sameTimeLastYear,
};

const debugLevel = process.env.REACT_APP_MDEBUG || 3;

// login handled by Amplify and Login component now

async function logout() {
  // get a call out to Amplify to log user out: https://github.com/aws-amplify/amplify-js/issues/614
  try {
    await signOut({ global: true });
    localStorage.removeItem('attributes');
  } catch (error) {
    console.log('error signing out: ', error);
  }
}

async function register(user) {
  if (debugLevel > 2) console.info('Auth.service > REGISTER: starting register for this dataset -->', JSON.stringify({ ...user, password: '********' }));
  const provider = user?.provider;
  let username = user?.username;
  let password = user?.password;

  // object user arrives as { user, password, email, preferred_username, tier, ... }
  // if (provider), this is the first attepmt of creating a federated user (to get the federated email); the rest is handled in federatedRegister (below)

  // because Cognito does not allow us to signUp with a preferred_username, we can only update it once the user is confirmed
  // until this moment, the username is going to be in custom:settings as "preferredUsername"

  try {
    // if we are trying to register with a provider, we need to do that first
    if (provider) {
      if (debugLevel > 2) console.info('Auth.service > REGISTER: federatedSignIn - stage I (do federatedSignIn and return extenrnal email) with parameter: ', provider);
      const response = await signInWithRedirect({ provider, customState: 'thisIsTestState' });
      if (debugLevel > 2) console.info('Auth.service > REGISTER: federatedSignIn - stage I successful; waiting for callback from Cognito');
      return response; // this will anyway result in a redirect
    }
  } catch (error) {
    // if there is no Cognito user for the external email, the above will fail
    // we are expecting to see the email as error code to be used in the next step
    console.log('Auth.service > REGISTER: error in federatedSignIn (1st attempt):', error);
    if (error.message.includes('EMAIL:')) {
      username = error.code;
      password = nanoid(); // since this is an external user signup, the password is not relevant; the user can get it reset later;
    } else {
      throw new Error(`Auth.service > REGISTER: error while federated sign in (stage I) -- ${error}`);
    }
  }

  let response;
  const userAttributes = {
    locale: user.locale || 'de',
    'custom:settings': JSON.stringify({
      baseCurrency: user.baseCurrency,
      baselineDate: defaultBaselineDate.date,
      namedBaseline: defaultBaselineDate.name,
    }),
    'custom:signup': JSON.stringify({
      preferredUsername: user.preferredUsername,
      tierId: user.tierId,
    }),
    address: JSON.stringify(user.address),
    'custom:stripe_customer_id': user.stripeCustomerId,
    'custom:tours_seen': JSON.stringify([]),
  };

  if (!user?.putEmailInCustomSignupAttribute) {
    userAttributes.email = user.email;
  } else {
    userAttributes.name = user.email;
  }

  try {
    if (debugLevel > 2) console.info('Auth.service > REGISTER: Cognito signUp with object: ', JSON.stringify({ ...userAttributes, password: '********' }));
    response = await signUp({
      username: user.username,
      password: user.password,
      options: {
        userAttributes,
      },
    });
    if (debugLevel > 2) console.info('Auth.service > REGISTER: Cognito signUp completed with response:', response);
  } catch (error) {
    console.error('Auth.service > REGISTER: error while signing up:', error);
    throw new Error(`Auth.service > REGISTER: signupFailed --> ${error}`);
  }

  return {
    ...user,
    status: 'awaitingEmailConfirmation',
    provider: 'internal',
  };
}

async function resendConfirmationEmail(user) {
  let response;
  try {
    const answer = await resendSignUpCode({ username: user.username });
    console.log(answer);
    // put the registration data into state for now
    response = {
      ...user,
      status: 'awaitingEmailConfirmation',
    };
  } catch (error) {
    console.log('Redux middleware: error while signing up:', error);
  }

  return response;
}

async function confirmUser(username, code) {
  let response;
  try {
    const responseConfirm = await confirmSignUp({ username, confirmationCode: code });
    console.log('Confirmation successful', responseConfirm);
    response = { status: 'confirmed' };
  } catch (error) {
    console.log('Redux middleware: error while confirming the new account:', error);
    if (error.code === 'NotAuthorizedException') {
      // it means the user is already confirmed
      response = { status: 'confirmed' };
    } else {
      throw new Error('Redux middleware: error while confirming the new account');
    }
  }

  return response;
}

async function updateUserAttribute(attributeName, attributeValueAsString) {
  try {
    await updateUserAttributes({
      userAttributes: {
        [attributeName]: attributeValueAsString,
      },
    });
  } catch (error) {
    // if the user is not authenticated, we can't set up Cognito, so carry on
    if (error !== 'The user is not authenticated' && error?.message !== 'The user is not authenticated') {
      throw new Error('cognitoUpdateFailed');
    }
  }
}

async function changePassword(currentPassword, newPassword) {
  let response;
  try {
    response = await updatePassword({ oldPassword: currentPassword, newPassword });
  } catch (error) {
    console.error(`changePassword error: ${error}`);
    // error code to be picked up by error handler of the action creator
    throw new Error(error.message); // 'Incorrect username or password'
  }
  return response;
}

async function refreshTokens() {
  try {
    const session = await getAuthSession(true);
    console.log('createSession: ', session);
    // this throws an "NotAuthorizedException: Invalid Refresh Token" error when token is wrong
  } catch (error) {
    console.log('Redux middleware: error trying to refresh session:', error);
  }
}

async function addSeenTours(payload) {
  // expects string
  if (typeof payload !== 'string') throw new Error('Expected parameter is id of the seen tour as string.');

  let initialToursSeen;
  let updatedToursSeen;
  try {
    const attributes = await getUserAttributes();
    if (attributes['custom:tours_seen']) {
      const toursSeenRaw = attributes['custom:tours_seen'] || '';
      initialToursSeen = JSON.parse(toursSeenRaw);
    } else initialToursSeen = [];

    initialToursSeen = [...initialToursSeen, payload];

    await updateUserAttributes({
      userAttributes: { 'custom:tours_seen': JSON.stringify(initialToursSeen) },
    });

    updatedToursSeen = initialToursSeen;
  } catch (error) {
    console.log('Redux middleware: error while updating user attributes:', error);
    throw new Error('Redux middleware: error while updating user attributes');
  }
  //
  return updatedToursSeen;
}

async function updateCognitoSettings(payload) {
  // overwrites settings from payload in Cognito
  // expects object with settings { targetBaseline, namedBaseline, ... }

  let settings;
  let updatedSettings;
  try {
    const attributes = await getUserAttributes();
    settings = JSON.parse(attributes['custom:settings']);
    updatedSettings = { ...settings, ...payload };

    // update Cognito
    await updateUserAttributes({ userAttributes: { 'custom:settings': JSON.stringify(updatedSettings) } });
    // after the update Amplify Hub has the latest version already
  } catch (error) {
    console.log('Redux middleware: error while updating user settings:', error);
    throw new Error('Redux middleware: error while updating user settings');
  }
  return payload;
}

export default {
  register,
  logout,
  confirmUser,
  changePassword,
  updateUserAttribute,
  resendConfirmationEmail,
  refreshTokens,
  addSeenTours,
  updateCognitoSettings,
};
